/**
 * My Rides › Archive — terminal/finished items moved off the main screen:
 * completed & cancelled rides, and resolved (declined/cancelled/completed)
 * requests. Two tabs mirror the main My Rides layout.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listIncomingBookings, listMyRides } from '../lib/api';
import { useRouter } from '../lib/router';
import { useT } from '../lib/i18n';
import { BackButton } from '../lib/BackButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { RideCard } from '../components/RideCard';
import { RequestCard } from '../components/RequestCard';
import { RetryCard } from '../components/RetryCard';
import { isBookingActive, isRideActive } from '../lib/lifecycle';

type Tab = 'rides' | 'requests';

export function MyRidesArchivePage(): JSX.Element {
  const { navigate, search } = useRouter();
  const { t } = useT();
  const initial: Tab = search.get('tab') === 'requests' ? 'requests' : 'rides';
  const [tab, setTab] = useState<Tab>(initial);

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />
      <h1 className="mb-3 text-2xl font-bold text-tg-text">{t('my.archive.title')}</h1>

      <div className="mb-4 grid grid-cols-2 gap-1 rounded-input bg-tg-bg-secondary p-1">
        {(['rides', 'requests'] as const).map((tk) => (
          <button
            key={tk}
            type="button"
            onClick={() => setTab(tk)}
            className={
              'rounded-input py-2 text-sm font-medium transition ' +
              (tab === tk ? 'bg-tg-bg text-tg-text shadow-sm' : 'text-tg-hint')
            }
          >
            {tk === 'rides' ? t('my.archive.tabRides') : t('my.archive.tabRequests')}
          </button>
        ))}
      </div>

      {tab === 'rides' ? (
        <ArchivedRides onOpen={(id) => navigate(`/ride/${id}`)} />
      ) : (
        <ArchivedRequests onOpen={(id) => navigate(`/booking/${id}`)} />
      )}
    </div>
  );
}

function ArchivedRides({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const { t } = useT();
  const q = useQuery({ queryKey: ['my-rides'], queryFn: listMyRides });
  if (q.isLoading) return <Skeleton />;
  if (q.isError)
    return (
      <RetryCard
        message={(q.error as Error).message}
        onRetry={() => q.refetch()}
        isRetrying={q.isFetching}
      />
    );
  const archived = (q.data?.items ?? []).filter((r) => !isRideActive(r.status));
  if (archived.length === 0) return <Empty title={t('my.archive.emptyRides')} />;
  return (
    <div className="space-y-3">
      {archived.map((r) => (
        <RideCard key={r.id} ride={r} hideDriver onOpen={() => onOpen(r.id)} />
      ))}
    </div>
  );
}

function ArchivedRequests({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const { t } = useT();
  const q = useQuery({ queryKey: ['incoming-bookings'], queryFn: listIncomingBookings });
  if (q.isLoading) return <Skeleton />;
  if (q.isError)
    return (
      <RetryCard
        message={(q.error as Error).message}
        onRetry={() => q.refetch()}
        isRetrying={q.isFetching}
      />
    );
  const archived = (q.data?.items ?? []).filter((b) => !isBookingActive(b.status));
  if (archived.length === 0) return <Empty title={t('my.archive.emptyRequests')} />;
  return (
    <div className="space-y-3">
      {archived.map((b) => (
        <RequestCard key={b.id} booking={b} onOpen={() => onOpen(b.id)} />
      ))}
    </div>
  );
}

function Empty({ title }: { title: string }): JSX.Element {
  return (
    <Card className="text-center">
      <CardTitle>{title}</CardTitle>
      <CardHint>—</CardHint>
    </Card>
  );
}

function Skeleton(): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
      <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
    </div>
  );
}
